import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Pagination,
  PaginationItem,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  createStudentsAPIEndpoint,
  EndPoints,
} from "../../../services/StudentAPIs";
import StudentDetailsDialog from "../components/Student/StudentDetailsDialog";
import StudentStatusToggle from "../components/Student/StudentStatusToggle";

import ExportQrCodesDialog from "../components/Codes/ExportQrCodesDialog";
import {
  CenterEndPoints,
  createGeneralAPIEndpoint,
} from "../../../services/General";
import StudentDialog from "../components/Student/StudentDialog";
import { enqueueSnackbar } from "notistack";
import { AssessmentOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

function Student() {
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [centers, setCenters] = useState([]);
  const [openExport, setOpenExport] = useState(false);
  const [studentDetails, setStudentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const navigate = useNavigate();
  const fetchStudents = (page, pageSize, search) => {
    createStudentsAPIEndpoint(EndPoints.searchStudents)
      .fetch({ params: { search, page: page + 1, pageSize } })
      .then((response) => {
        setStudents(response.data.items || []);
        setTotalCount(response.data.totalCount || 0);
      })
      .catch((error) => {
        console.error("Error fetching student data:", error);
        setStudents([]);
      });
  };
  const fetchStudentDetails = async (studentId) => {
    setDetailsLoading(true);

    try {
      const response = await createStudentsAPIEndpoint(
        EndPoints.getStudentById(studentId),
      ).fetch();

      setStudentDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch student details:", error);

      enqueueSnackbar(
        error.response?.data?.message || "Failed to load student details.",
        { variant: "error" },
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    createGeneralAPIEndpoint(CenterEndPoints.all)
      .fetch()
      .then((response) => {
        setCenters(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching centers:", error);
        setCenters([]);
      });
  }, []);

  useEffect(() => {
    fetchStudents(page, pageSize, search);
  }, [page, pageSize, search]);

  const handleViewClick = async (student) => {
    setOpenDetailsDialog(true);
    setDetailsLoading(true);
    try {
      const response = await createStudentsAPIEndpoint(
        EndPoints.getStudentById(student.id),
      ).fetch();
      setStudentDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch student details:", error);
      setOpenDetailsDialog(false);

      enqueueSnackbar(
        error.response?.data?.message || "Failed to load student details.",
        { variant: "error" },
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDetailsDialog(false);
    setSelectedStudent(null);
  };

  const handleAddClick = () => {
    setSelectedStudent(null);
    setOpenStudentDialog(true);
  };
  const handleEditClick = async (student) => {
    setDetailsLoading(true);

    try {
      const response = await createStudentsAPIEndpoint(
        EndPoints.getStudentById(student.id),
      ).fetch();

      setSelectedStudent(response.data);
      setOpenStudentDialog(true);
    } catch (error) {
      console.error(error);

      enqueueSnackbar(
        error.response?.data?.message || "Failed to load student.",
        { variant: "error" },
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <section className="dashboard-section">
      <div className="table-wrapper">
        <TableContainer
          component={Paper}
          sx={{ margin: "2rem auto" }}
          className="table-container"
        >
          <Box className="table-box">
            <TextField
              label="Search"
              variant="standard"
              size="large"
              value={search}
              onChange={(e) => {
                setPage(0);
                setSearch(e.target.value);
              }}
              className="search-field"
            />
            <Button
              className="btn-add-record"
              variant="contained"
              onClick={handleAddClick}
            >
              Add New Student
            </Button>
            <Button
              variant="contained"
              className="btn-export-qr"
              onClick={() => setOpenExport(true)}
            >
              Export QR Codes
            </Button>
          </Box>
          <div className="table-title">Students Data</div>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell className="table-header">
                  <b>Full Name</b>
                </TableCell>
                <TableCell className="table-header">
                  <b>Education Stage</b>
                </TableCell>
                <TableCell className="table-header">
                  <b>Center Name</b>
                </TableCell>
                <TableCell className="table-header">
                  <b>Status</b>
                </TableCell>
                <TableCell className="table-header">
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(students) && students.length !== 0 ? (
                students.map((student) => (
                  <TableRow key={student.Id} className="table-row">
                    <TableCell className="table-cell">
                      {student.fullName}
                    </TableCell>
                    <TableCell className="table-cell">
                      {student.educationStage}
                    </TableCell>
                    <TableCell className="table-cell">
                      {student.centerName}
                    </TableCell>
                    <TableCell className={`table-cell status-cell ${student.isActive ? 'active' : 'inactive'}`}>
                      {student.isActive ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell className="table-cell action-cell">
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          className="btn-view"
                          onClick={() => handleViewClick(student)}
                        >
                          View
                        </Button>

                        <Button
                          className="btn-edit"
                          onClick={() => handleEditClick(student)}
                        >
                          Edit
                        </Button>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<AssessmentOutlined />}
                        onClick={() =>
                          navigate(`/students/${student.id}/monthly-report`)
                        }
                      >
                        Report
                      </Button>
                      <StudentStatusToggle
                        student={student}
                        fetchStudents={fetchStudents}
                        page={page}
                        pageSize={pageSize}
                        search={search}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="table-cell">
                    No students found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination
            className="pagination"
            page={page + 1}
            count={Math.ceil(totalCount / pageSize)}
            onChange={(event, newPage) => setPage(newPage - 1)}
            renderItem={(item) => <PaginationItem component="div" {...item} />}
          />
          <StudentDetailsDialog
            openDialog={openDetailsDialog}
            handleCloseDialog={handleCloseDialog}
            selectedStudent={studentDetails}
          />
          <StudentDialog
            open={openStudentDialog}
            onClose={() => {
              setOpenStudentDialog(false);
              setSelectedStudent(null);
            }}
            selectedStudent={selectedStudent}
            centers={centers}
            onStudentUpdated={() => {
              setOpenStudentDialog(false);
              setSelectedStudent(null);
              fetchStudents(page, pageSize, search);
            }}
          />
          <ExportQrCodesDialog
            open={openExport}
            onClose={() => setOpenExport(false)}
            students={students}
          />
        </TableContainer>
      </div>
    </section>
  );
}

export default Student;
